
import {
  Color, Interval,
} from '@/model';
import labels from '@/store/labels';
import { notEmpty } from '@/util/array-util';
import { traverse } from '@/util/functional-iterator';
import { GroupedInterval, groups } from '@/util/text-selection';
import Vue from 'vue';

const selectionOpacity = 0.5;
const highlightOpacity = 0.9;

export interface Clip extends Interval {
  labels?: number[];
  base?: boolean;
  id?: number;
  show: boolean;
}

export default Vue.extend({
  name: 'LabeledText',

  props: {
    text: { type: String, required: true },
    intervals: { type: Array as () => Array<Interval & { labels?: number[]; show: boolean }>, required: true },
    highlight: { type: Array as () => number[], default: () => [] },
    defaultSelectionColor: { type: Object as () => Color | null, default: () => ({ r: 168, g: 209, b: 255 }) },
    baseStyle: { type: Object, required: false },
    colors: { type: Boolean, default: true },
  },

  watch: {
    clips: {
      immediate: true,
      handler(clips) {
        this.$emit('clipsChanged', clips);
      },
    },
  },

  computed: {
    clips(): GroupedInterval<Partial<Clip>>[] {
      if (!this.text) {
        return [];
      }
      const base = { base: true, from: 0, to: this.text?.length };
      return groups<Partial<Clip>>([base, ...this.intervals]);
    },
    containerTop(): number | undefined {
      const box = traverse(this.$el, (e) => e.parentElement || undefined).find((e) => e.classList.contains('box'));
      return this.elementTop(box);
    },
  },

  methods: {
    textOf(interval: Interval) {
      return this.text?.substring(interval.from, interval.to);
    },
    clipStyle(clip?: GroupedInterval<Partial<Clip>>) {
      const base = clip?.group.length === 1 && !!clip?.group.find((g) => g.base === true);
      if (base) {
        return this.baseStyle;
      }
      const rgbs = clip?.group
        .flatMap((g) => {
          if (g.base === true) {
            return [];
          }
          if (g.show === false) {
            return [];
          }
          const highlight = !!this.highlight.find((h) => h === g.id);
          let colors: Color[];
          if (!this.colors || !g.labels || g.labels.length === 0) {
            colors = this.defaultSelectionColor ? [this.defaultSelectionColor] : [];
          } else {
            colors = g.labels.map((l) => labels.getters.lookup.get(l)?.color).filter(notEmpty);
          }
          return colors.map((c) => `rgba(${c?.r},${c?.g},${c?.b},${highlight ? highlightOpacity : selectionOpacity})`);
        })
        .filter(notEmpty) || [];
      if (rgbs.length === 0) {
        return {};
      } else if (rgbs.length === 1) {
        return {
          background: `${rgbs[0]}`,
        };
      } else {
        const ratio = 100 / rgbs.length;
        const gradients = rgbs.map((c, i) => `${c} ${Math.max(0, ratio * i)}%,${c} ${Math.min(100, ratio * (i + 1))}%`);
        return {
          background: `repeating-linear-gradient(45deg, ${gradients.join(', ')}) 0% 0% / 20px`,
        };
      }
    },
    randomColor(): Color {
      const component = () => Math.floor(Math.random() * 256);
      return {
        r: component(), g: component(), b: component(),
      };
    },
    elementTop(elem?: Element): number | undefined {
      const rect = elem?.getBoundingClientRect();
      if (!rect || rect.top === 0) {
        return undefined;
      }
      return rect.top + window.pageYOffset;
    },
    clicked(clip: GroupedInterval<Partial<Clip>>) {
      this.$emit('clipClicked', clip);
    },
  },
});
